import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { CompanyLogo, getDomainFromCompany } from '../ui/CompanyLogo';
import { connectorsApi } from '../../services/connectorsApi';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import useAIChatStreamHandler from '../agno/hooks/useAIChatStreamHandler';
import useStore from '../agno/store';
import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingDraft {
  draft_id: string;
  provider: string;
  provider_display_name?: string;
  action: string;
  created_at: number; // unix seconds
  account_email?: string;
  summary?: string;
  editable_fields?: Record<string, unknown>;
}

interface PendingApprovalsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** When set, the drawer scrolls to and highlights this draft on open. */
  focusDraftId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(unixSeconds: number): string {
  const diffSec = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

const STALE_THRESHOLD_SECONDS = 15 * 60; // 15 min

function isStale(draft: PendingDraft): boolean {
  return Date.now() / 1000 - draft.created_at > STALE_THRESHOLD_SECONDS;
}

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface DraftRowProps {
  draft: PendingDraft;
  isFocused: boolean;
  isHighlighted: boolean;
  onApprove: (draftId: string) => void;
  onReject: (draftId: string) => void;
  approving: boolean;
  rejecting: boolean;
}

const DraftRow = React.forwardRef<HTMLDivElement, DraftRowProps>(
  ({ draft, isFocused, isHighlighted, onApprove, onReject, approving, rejecting }, ref) => {
    const stale = isStale(draft);
    const displayName = draft.provider_display_name ?? draft.provider;
    const domain = getDomainFromCompany(displayName);
    const busy = approving || rejecting;

    return (
      <div
        ref={ref}
        tabIndex={0}
        className={cn(
          'flex items-center gap-3 rounded-lg border px-3 py-3 transition-colors outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          isHighlighted
            ? 'border-ring ring-2 ring-ring/30 bg-muted/40 animate-pulse-once'
            : isFocused
              ? 'border-border-strong bg-muted/40'
              : 'border-border bg-card',
        )}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !busy) onApprove(draft.draft_id);
        }}
        aria-label={`Draft: ${draft.summary ?? draft.action} from ${displayName}`}
      >
        {/* Provider logo */}
        <CompanyLogo
          domain={domain}
          name={displayName}
          className="h-8 w-8 flex-shrink-0"
        />

        {/* Content */}
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-foreground"
            title={draft.summary ?? draft.action}
          >
            {draft.summary ?? draft.action}
          </p>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-border-chip bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {displayName}
            </span>
            <span
              className={cn(
                'flex items-center gap-1 text-[11px]',
                stale ? 'text-status-cancelled' : 'text-muted-foreground',
              )}
            >
              <Clock size={10} />
              {timeAgo(draft.created_at)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReject(draft.draft_id)}
            disabled={busy}
            className="h-7 gap-1 px-2 text-muted-foreground hover:text-status-cancelled"
            aria-label="Reject"
          >
            <X size={12} />
            Reject
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onApprove(draft.draft_id)}
            disabled={busy}
            className="h-7 gap-1 px-2"
            aria-label="Approve"
          >
            {approving ? (
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Check size={12} />
            )}
            Approve
          </Button>
        </div>
      </div>
    );
  },
);
DraftRow.displayName = 'DraftRow';

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 15_000;

export function PendingApprovalsDrawer({ open, onClose, focusDraftId }: PendingApprovalsDrawerProps) {
  const workspaceId =
    useWorkspaceStore((s) => s.workspace?.id) ||
    (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);
  const sessionId = useStore((s) => s.sessionId);
  const { handleStreamResponse } = useAIChatStreamHandler();

  const [drafts, setDrafts] = useState<PendingDraft[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState<number>(0);
  const [pendingOps, setPendingOps] = useState<Record<string, 'approving' | 'rejecting'>>({});
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [highlightedDraftId, setHighlightedDraftId] = useState<string | undefined>(undefined);

  // Reset the load-state gate whenever the drawer closes so the next open
  // doesn't flash a stale "no longer pending" hint before the first fetch.
  useEffect(() => {
    if (!open) {
      setHasLoaded(false);
      setHighlightedDraftId(undefined);
    }
  }, [open]);

  const fetchDrafts = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await connectorsApi.getPendingDrafts(String(workspaceId));
      const data = (res as any)?.data;
      const list: PendingDraft[] = Array.isArray(data?.drafts) ? data.drafts : [];
      // Sort freshest first
      list.sort((a, b) => b.created_at - a.created_at);
      setDrafts(list);
    } catch (err: any) {
      // 404 means endpoint not yet deployed — treat as empty
      if (err?.response?.status === 404 || err?.status === 404) {
        setDrafts([]);
      } else {
        // Silently fail for other errors; don't flash an error toast during polling
        console.warn('[PendingApprovalsDrawer] fetch failed', err?.message);
      }
    } finally {
      setHasLoaded(true);
    }
  }, [workspaceId]);

  // Fetch on mount and when drawer opens
  useEffect(() => {
    if (!open) return;
    fetchDrafts();
  }, [open, fetchDrafts]);

  // Poll every 15s while open
  useEffect(() => {
    if (!open) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }
    pollTimerRef.current = setInterval(fetchDrafts, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [open, fetchDrafts]);

  // Refetch on window focus
  useEffect(() => {
    if (!open) return;
    const handler = () => fetchDrafts();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, [open, fetchDrafts]);

  // When focusDraftId changes (or drafts load), scroll to + highlight that row
  useEffect(() => {
    if (!open || !focusDraftId) return;
    const idx = drafts.findIndex((d) => d.draft_id === focusDraftId);
    if (idx === -1) {
      // Draft not in list — show the "no longer pending" fallback (handled in render)
      setHighlightedDraftId(focusDraftId);
      return;
    }
    setHighlightedDraftId(focusDraftId);
    setFocusedIdx(idx);
    // Scroll into view on next frame so the DOM is settled
    requestAnimationFrame(() => {
      rowRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  // Re-run whenever focusDraftId or the drafts list changes size
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusDraftId, drafts.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, drafts.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, drafts.length, onClose]);

  // Focus the active row when focusedIdx changes
  useEffect(() => {
    rowRefs.current[focusedIdx]?.focus();
  }, [focusedIdx]);

  const sendUserMessage = (text: string) => {
    handleStreamResponse(text).catch((err: unknown) => {
      console.error('[PendingApprovalsDrawer] sendUserMessage failed', err);
    });
  };

  const handleApprove = async (draftId: string) => {
    if (!workspaceId) return;
    const draft = drafts.find((d) => d.draft_id === draftId);
    if (!draft) return;

    setPendingOps((prev) => ({ ...prev, [draftId]: 'approving' }));
    // Optimistic remove
    setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));

    try {
      await connectorsApi.approveDraft(
        String(workspaceId),
        draftId,
        {},
        sessionId ?? undefined,
      );
      sendUserMessage(
        `[connector_action] approved draft draft_id=${draftId} provider=${draft.provider} action=${draft.action}`,
      );
    } catch (err: any) {
      // Revert optimistic removal
      setDrafts((prev) => {
        const exists = prev.some((d) => d.draft_id === draftId);
        return exists ? prev : [draft, ...prev].sort((a, b) => b.created_at - a.created_at);
      });
      toast.error('Failed to approve draft. Please try again.');
    } finally {
      setPendingOps((prev) => {
        const next = { ...prev };
        delete next[draftId];
        return next;
      });
    }
  };

  const handleReject = async (draftId: string) => {
    if (!workspaceId) return;
    const draft = drafts.find((d) => d.draft_id === draftId);
    if (!draft) return;

    setPendingOps((prev) => ({ ...prev, [draftId]: 'rejecting' }));
    // Optimistic remove
    setDrafts((prev) => prev.filter((d) => d.draft_id !== draftId));

    try {
      await connectorsApi.rejectDraft(
        String(workspaceId),
        draftId,
        undefined,
        sessionId ?? undefined,
      );
      // Reject is terminal — no follow-up agent message needed
    } catch (err: any) {
      // Revert optimistic removal
      setDrafts((prev) => {
        const exists = prev.some((d) => d.draft_id === draftId);
        return exists ? prev : [draft, ...prev].sort((a, b) => b.created_at - a.created_at);
      });
      toast.error('Failed to reject draft. Please try again.');
    } finally {
      setPendingOps((prev) => {
        const next = { ...prev };
        delete next[draftId];
        return next;
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="right"
        className="flex w-[420px] max-w-full flex-col gap-0 p-0 sm:max-w-[420px]"
      >
        {/* Header */}
        <SheetHeader className="flex flex-row items-center gap-2 border-b border-border px-5 py-4">
          <SheetTitle className="flex-1 text-base">Pending approvals</SheetTitle>
          {drafts.length > 0 && (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                drafts.some(isStale)
                  ? 'bg-status-cancelled/[0.12] text-status-cancelled'
                  : 'bg-status-in-progress/[0.12] text-status-in-progress',
              )}
            >
              {drafts.length}
            </span>
          )}
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {(() => {
            // Three mutually exclusive states:
            //  1. Still loading the first batch — show a spinner
            //  2. We have a focused draft but it's not in the (now-loaded) list
            //     → show the stale-draft hint ONLY (no "no pending approvals" underneath)
            //  3. Drafts present → render them
            //  4. Truly empty → show the empty state
            if (!hasLoaded) {
              return (
                <div className="flex items-center justify-center py-12">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
                </div>
              );
            }
            const staleFocus =
              highlightedDraftId &&
              !drafts.some((d) => d.draft_id === highlightedDraftId);
            if (staleFocus && drafts.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Check size={28} className="mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">
                    Draft{' '}
                    <code className="font-mono text-[11px]">{highlightedDraftId}</code>{' '}
                    is no longer pending.
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground/70">
                    It may have been approved, rejected, or expired.
                  </p>
                </div>
              );
            }
            if (drafts.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Check size={28} className="mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No pending approvals</p>
                </div>
              );
            }
            return (
              <>
                {staleFocus && (
                  <p className="mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
                    Draft <code className="font-mono">{highlightedDraftId}</code> is no longer pending.
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  {drafts.map((draft, idx) => (
                    <DraftRow
                      key={draft.draft_id}
                      ref={(el) => { rowRefs.current[idx] = el; }}
                      draft={draft}
                      isFocused={focusedIdx === idx}
                      isHighlighted={draft.draft_id === highlightedDraftId}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      approving={pendingOps[draft.draft_id] === 'approving'}
                      rejecting={pendingOps[draft.draft_id] === 'rejecting'}
                    />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default PendingApprovalsDrawer;
